// Creator picks — the data layer for the #/admin panel (campaign T57 WP0-B).
//
// John pairs an Instagram post to a venue and a creator, by hand. There is no
// Meta credential in WP0 and this module makes no network call to Instagram
// (decision D-10): every field is something he pasted or typed.
//
// Contract: META_INSTAGRAM_CONTRACT.md v1.1 §1.1 (table), §1.2 (creator
// identity), §2.1 (card shape), §3 (this panel). Amended by LEAD RULING R1.
//
// AUTHORISATION. Nothing here is a security boundary. `creator_recommendations`
// is superAdmin-only for INSERT/UPDATE/DELETE via `public.is_super_admin()`, and
// `provider_profiles` INSERT is gated by the live "SuperAdmins create provider
// profiles" policy. A tampered client gets a Postgres error, not data — which is
// why this file can afford to surface errors rather than pre-empt them.
//
// ── THREE AS-BUILT FACTS THIS MODULE IS SHAPED BY (WP0A_HANDOVER §8) ─────────
//
//  1. The table is COLUMN-restricted. `authenticated` holds SELECT on an
//     explicit allow-list only, so `select('*')` returns a 42501, not a row.
//     `note` and `created_by` are NOT readable back by this console even though
//     it writes them — they are operator-private in both senses. Hence
//     PICK_COLUMNS below, and hence Recent picks cannot show the note.
//  2. The deactivate UPDATE must NOT chain `.select()`. supabase-js switches to
//     `Prefer: return=representation` the moment you do, which puts the write
//     back inside the SELECT policy and re-opens the Q-1 refusal from a
//     different direction. Bare `.update()` only.
//  3. `created_by` is TEXT (the `provider_profiles.user_id` convention), not
//     uuid. Send `session.user.id` as a string; never cast it.
//
// Verified read-only against MAIN (dhoendruytdhwoietctm) while writing this:
// `business_type` has no CHECK constraint; `saoirse_excluded` defaults to FALSE
// (so it must be sent explicitly); `business_name` is the only NOT NULL column
// on `provider_profiles` without a default; `user_id` is UNIQUE and nullable;
// `admin_list_providers(p_query text, p_limit int)` is the live signature.

import { supabase } from './supabase';
import { canonicalPostUrl, normaliseHandle } from './handles';
import { creatorInvariantBreach } from './creator-invariant';

/**
 * The consumer-readable projection of `creator_recommendations`, verbatim.
 *
 * This list is the contract §2.1 card columns plus `is_active`, and it is
 * exactly what the migration grants:
 *
 *   GRANT SELECT (id, provider_id, creator_id, source, attribution, source_url,
 *                 caption_excerpt, thumbnail_url, media_type, posted_at,
 *                 created_at, is_active) ... TO authenticated;
 *
 * Adding a column here that is not in that grant turns every read into a 42501.
 * `note`, `created_by`, `source_post_id`, `verified_at`, `landmark_id` and
 * `updated_at` are deliberately absent.
 */
const PICK_COLUMNS =
  'id, provider_id, creator_id, source, attribution, source_url, ' +
  'caption_excerpt, thumbnail_url, media_type, posted_at, created_at, is_active';

/** Contract §1.1: `caption_excerpt` is capped at 280 chars, in the console. */
export const EXCERPT_MAX = 280;

/**
 * A creator: a `provider_profiles` row with `business_type = 'influencer'`.
 *
 * `latitude`/`longitude` are carried purely so the panel can check the R2/G-2
 * invariant — they are never written by this console and must always be NULL on
 * a creator (contract §1.2).
 */
export interface CreatorRow {
  id: string;
  business_name: string;
  instagram_handle: string | null;
  logo_url: string | null;
  is_active: boolean | null;
  saoirse_excluded: boolean | null;
  latitude: number | null;
  longitude: number | null;
}

/** A venue candidate from `admin_list_providers`, after the influencer filter. */
export interface VenueRow {
  id: string;
  business_name: string;
  business_type: string | null;
  is_active: boolean;
  claimed_at: string | null;
}

/** One `creator_recommendations` row, in the columns this console may read. */
export interface PickRow {
  id: string;
  provider_id: string;
  creator_id: string;
  source: string;
  attribution: string;
  source_url: string;
  caption_excerpt: string | null;
  thumbnail_url: string | null;
  media_type: string | null;
  posted_at: string | null;
  created_at: string;
  is_active: boolean;
}

/**
 * A pick with the two profile names resolved for display.
 *
 * `creator_handle` is the RAW stored `provider_profiles.instagram_handle`, not a
 * normalised copy. That is deliberate: `query_creator_recs_for_provider` renders
 * the handle with Postgres' own `lower(ltrim(…,'@'))`, which is looser than
 * `normaliseHandle` — it has no character-class gate and no length cap. So a
 * stored handle this console cannot normalise is still one Curate WILL show, and
 * silently rendering "no handle" here would be the console lying about what a
 * traveller sees. The panel shows the raw value flagged instead.
 */
export interface RecentPick extends PickRow {
  provider_name: string;
  creator_name: string;
  creator_handle: string | null;
}

const CREATOR_COLUMNS =
  'id, business_name, instagram_handle, logo_url, is_active, saoirse_excluded, latitude, longitude';

/** Postgres/PostgREST error surface, as supabase-js hands it over. */
interface WireError {
  message?: string;
  details?: string | null;
  hint?: string | null;
  code?: string | null;
}

/**
 * Turn a database refusal into a sentence John can act on — WITHOUT hiding it.
 *
 * House rule: never swallow. The server's own message always survives, and the
 * server's HINT is surfaced too, because the URL trigger puts its most useful
 * advice there ("open the post and copy the URL from the address bar"). The
 * friendly lead is added in front, never instead.
 *
 * The two constraint names are the ones WP0-A pinned so they can be recognised
 * from the client: `creator_recommendations_bad_url` and
 * `creator_recommendations_bad_roles`.
 */
function describeError(error: WireError | null, fallback: string): Error {
  if (!error) return new Error(fallback);
  const raw = error.message ?? fallback;
  const parts: string[] = [];

  if (error.code === '23505' || /uq_creator_recs_post_venue/.test(raw)) {
    // The index is partial (R1/Q-2), so this means an ACTIVE pick already holds
    // this (post, venue). Re-adding is not the recovery — deactivating that row,
    // or reactivating it from Recent picks, is. Saying "pair it to a different
    // venue" was wrong on the Reactivate path, where that is not on offer.
    parts.push('That post is already paired to this venue by an active pick. '
      + 'Deactivate that pick first, or reactivate it from Recent picks below.');
  } else if (/creator_recommendations_bad_url/.test(raw)) {
    parts.push('That is not a permalink the database will store.');
  } else if (/creator_recommendations_bad_roles/.test(raw)) {
    parts.push('The creator must be an influencer profile and the venue must not be.');
  } else if (error.code === '42501') {
    // Reads hit this too (listCreators, listRecentPicks, searchVenues), so the
    // lead must not say "write", and it must not assert the session is fine.
    parts.push('The database refused that request. Either this session is not the operator '
      + 'it requires, or the migration that grants it has not been applied yet.');
  } else if (error.code === 'PGRST202' || /Could not find the function/i.test(raw)) {
    // The ONLY RPC this module calls is admin_list_providers, which is live and
    // predates this campaign. Blaming the HELD migration here sent the operator
    // to the wrong place entirely.
    parts.push('The database could not find the function this panel called. Its schema cache '
      + 'may be stale, or this session is not the operator.');
  } else if (
    error.code === 'PGRST205'
    || /Could not find the table/i.test(raw)
    || (error.code === '42P01' && /creator_recommendations/.test(raw))
  ) {
    // PGRST205 is what PostgREST actually answers for a table missing from the
    // schema cache, which is the guaranteed state until the migration is applied.
    // A bare /does not exist/i catch-all used to sit here and would have
    // mislabelled any "column ... does not exist" as a HELD migration.
    parts.push('The creator_recommendations table is not there yet — its migration is '
      + 'still HELD and has to be applied before this panel can read or write anything. '
      + "Ignore any \"Perhaps you meant…\" suggestion below; there is no other table.");
  }

  parts.push(raw);
  if (error.hint) parts.push(`Hint: ${error.hint}`);
  if (error.details) parts.push(error.details);
  return new Error(parts.join(' '));
}

/** The signed-in operator's auth uid, as TEXT. Throws if the session has gone. */
async function currentUserId(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const uid = data.session?.user.id;
  if (!uid) throw new Error('Your session expired — sign in again.');
  return uid;
}

// ── Creators ────────────────────────────────────────────────────────────────

/** Every influencer profile, by name. Inactive ones are included and marked. */
export async function listCreators(): Promise<CreatorRow[]> {
  const { data, error } = await supabase
    .from('provider_profiles')
    .select(CREATOR_COLUMNS)
    .eq('business_type', 'influencer')
    .order('business_name', { ascending: true })
    .limit(200);
  if (error) throw describeError(error, 'Could not load creators.');
  return (data ?? []) as unknown as CreatorRow[];
}

/**
 * Create an unclaimed creator profile — contract §1.2 defaults, exactly.
 *
 * `user_id` stays NULL so the creator can claim the row later through the
 * existing `#/claim` code flow, and `saoirse_excluded` is TRUE so the Saoirse
 * selection never treats a creator as a venue.
 *
 * ⚠️ THE `saoirse_excluded` ASSERTION IS NOT DEFENSIVE PROGRAMMING — it is the
 * only detector for a specific silent failure. `trg_protect_saoirse_dial` is
 * BEFORE INSERT OR UPDATE but RAISES only on UPDATE; on INSERT an unprivileged
 * writer gets **silently coerced** to `false` and the row lands looking
 * perfectly fine (WP0A_HANDOVER §8.3, measured). If this console ever stops
 * running as superAdmin, every creator it creates would quietly become
 * Saoirse-visible — a creator offered to a traveller as somewhere to eat. So we
 * read the flag back and refuse to call it a success if it did not stick.
 *
 * `city_id` is NULL: no Florence city row is referenced anywhere in this console
 * (grepped — `city_id` appears only in `#/edit`, where it is read for display),
 * so the brief's "else null" branch is the live one.
 */
export async function addCreator(input: { name: string; handle: string | null }): Promise<CreatorRow> {
  const name = input.name.trim();
  if (name === '') throw new Error('A creator needs a display name.');

  // A handle is optional (R1: creators may have none, and consumers fall back to
  // the name) — but a handle that was TYPED and did not survive normalisation is
  // a typo, and saying so beats storing null and pretending it was fine.
  const rawHandle = (input.handle ?? '').trim();
  const handle = rawHandle === '' ? null : normaliseHandle(rawHandle);
  if (rawHandle !== '' && handle === null) {
    throw new Error(`"${rawHandle}" is not an Instagram handle I can read. `
      + 'Use the handle itself (florencefoodie), or paste their profile URL.');
  }

  const { data, error } = await supabase
    .from('provider_profiles')
    .insert({
      business_name: name,
      business_type: 'influencer',
      instagram_handle: handle,
      is_active: true,
      saoirse_excluded: true,
      user_id: null,
      city_id: null,
    })
    .select(CREATOR_COLUMNS)
    .single();

  if (error) throw describeError(error, 'Could not create that creator.');
  const row = data as unknown as CreatorRow;

  // Same predicate the Creators panel badges with (R2/G-2(a)), so a row that
  // lands broken is reported the same way whether it arrived through this form
  // or through an approved #/apply application.
  const breach = creatorInvariantBreach(row);
  if (breach) {
    throw new Error(
      `SAOIRSE DIAL DID NOT STICK. Creator "${row.business_name}" was created (id ${row.id}) `
      + `but it came back with ${breach.reasons.join(' and ')}, which means this write did not `
      + 'run as the operator. That profile can now be selected as a VENUE by the tour pipeline. '
      + 'Use Fix on its row in Creators, or remove it, before adding another creator.',
    );
  }
  return row;
}

/**
 * Restore the R2/G-2 invariant on one creator row.
 *
 * `UPDATE provider_profiles SET saoirse_excluded=true, latitude=NULL, longitude=NULL`,
 * exactly as the ruling specifies. Both BEFORE UPDATE triggers on the table
 * (`protect_saoirse_dial` and `protect_provider_profile_columns`) early-return
 * for a superAdmin, so this passes for the operator and raises for anyone else —
 * which is the correct fail-closed shape for a write that un-hides a row from
 * the tour pipeline.
 *
 * `{ count: 'exact' }` and no chained `.select()`, for the same reason as
 * `setPickActive`: keep `Prefer: return=minimal` while still learning whether a
 * row actually moved, so a silent zero-row update cannot be reported as a fix.
 */
export async function repairCreator(id: string): Promise<void> {
  const { error, count } = await supabase
    .from('provider_profiles')
    .update({ saoirse_excluded: true, latitude: null, longitude: null }, { count: 'exact' })
    .eq('id', id);
  if (error) throw describeError(error, 'Could not repair that creator.');
  if (count === 0) {
    throw new Error(
      'That creator did not change — the database matched no row. Either it has just been '
      + 'removed, or this session is not the operator the row-level policy requires.',
    );
  }
}

// ── Venues ──────────────────────────────────────────────────────────────────

/**
 * Venue candidates for the pairing form — contract §3: active, non-influencer.
 *
 * `admin_list_providers` is SECURITY DEFINER and superAdmin-gated inside the
 * function, so a non-operator gets `insufficient_privilege` rather than a
 * filtered list. It returns `business_type`, which is what makes the R-1 filter
 * below cheap and client-side: a creator must never be offered as a venue.
 *
 * The limit is 20 per the brief. The influencer filter runs AFTER that limit, so
 * a search term matching many creators returns fewer than 20 venues; at WP0
 * volumes (John is adding his first creators by hand) that is not worth a
 * second round trip, but it is why the panel shows what it filtered.
 */
export async function searchVenues(query: string): Promise<VenueRow[]> {
  const q = query.trim();
  if (q.length < 2) return [];
  const { data, error } = await supabase.rpc('admin_list_providers', {
    p_query: q,
    p_limit: 20,
  });
  if (error) throw describeError(error, 'Could not search providers.');
  return ((data ?? []) as VenueRow[])
    .filter((r) => r.business_type !== 'influencer' && r.is_active);
}

// ── Picks ───────────────────────────────────────────────────────────────────

export interface NewPick {
  postUrl: string;
  providerId: string;
  creatorId: string;
  excerpt: string;
  note: string;
}

/**
 * Pair a post to a venue and a creator.
 *
 * The URL is canonicalised client-side so the form can show John the exact
 * string about to be stored, but the BEFORE trigger re-canonicalises whatever we
 * send — it, not this, is the authority.
 *
 * `created_at`/`updated_at` are deliberately NOT sent: they are client-settable
 * on INSERT, and a row inserted with a future `created_at` would pin itself to
 * the top of the newest-first card list forever (WP0A_HANDOVER §8.11).
 */
export async function addPick(input: NewPick): Promise<void> {
  const canonical = canonicalPostUrl(input.postUrl);
  if (!canonical.ok) throw new Error(`${canonical.reason} ${canonical.hint}`);
  if (!input.providerId) throw new Error('Choose a venue for this pick.');
  if (!input.creatorId) throw new Error('Choose a creator for this pick.');
  if (input.providerId === input.creatorId) {
    // The roles trigger raises `a provider cannot recommend itself`; saying it
    // here costs nothing and names the two fields John has to change.
    throw new Error('The venue and the creator cannot be the same profile.');
  }

  const excerpt = input.excerpt.trim();
  if (excerpt.length > EXCERPT_MAX) {
    throw new Error(`"What they recommended" is ${excerpt.length} characters — `
      + `the limit is ${EXCERPT_MAX}.`);
  }
  const note = input.note.trim();

  const { error } = await supabase.from('creator_recommendations').insert({
    provider_id: input.providerId,
    creator_id: input.creatorId,
    source: 'manual',
    attribution: 'operator',
    source_url: canonical.url,
    caption_excerpt: excerpt === '' ? null : excerpt,
    note: note === '' ? null : note,
    created_by: await currentUserId(),
  });
  if (error) throw describeError(error, 'Could not save that pick.');
}

/**
 * The last 50 picks, active AND inactive — LEAD RULING R1 / Q-1.
 *
 * The superAdmin's second permissive SELECT policy is what makes the inactive
 * rows visible at all; a non-operator would see only `is_active` ones. The panel
 * greys the inactive rows and offers Reactivate.
 *
 * TWO SELECTS, NOT A POSTGREST EMBED. Both `provider_id` and `creator_id` are
 * FKs to the same table, so an embed needs a disambiguating hint and there is no
 * way to verify the exact spelling of it this session — the table does not exist
 * anywhere yet (the migration is HELD and there is no local stack on this Mac).
 * A plain `.in('id', …)` lookup on `provider_profiles` is a pattern this console
 * already runs in production, so it is the one that cannot be wrong for a reason
 * nobody can currently test. The cost is one extra round trip for ≤ 50 rows.
 *
 * Ordering mirrors the RPC's `created_at DESC, id DESC` so the console and
 * Curate agree on what "newest" means, ties included.
 */
export async function listRecentPicks(): Promise<RecentPick[]> {
  const { data, error } = await supabase
    .from('creator_recommendations')
    .select(PICK_COLUMNS)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(50);
  if (error) throw describeError(error, 'Could not load recent picks.');

  const picks = (data ?? []) as unknown as PickRow[];
  if (picks.length === 0) return [];

  const ids = [...new Set(picks.flatMap((p) => [p.provider_id, p.creator_id]))];
  const { data: profiles, error: profileError } = await supabase
    .from('provider_profiles')
    .select('id, business_name, instagram_handle')
    .in('id', ids);
  if (profileError) throw describeError(profileError, 'Could not load the paired profiles.');

  const byId = new Map(
    (profiles ?? []).map((p) => {
      const row = p as unknown as { id: string; business_name: string; instagram_handle: string | null };
      return [row.id, row];
    }),
  );

  return picks.map((pick) => {
    const venue = byId.get(pick.provider_id);
    const creator = byId.get(pick.creator_id);
    return {
      ...pick,
      // A name we cannot resolve is shown as such rather than as an empty cell:
      // it means the profile was deleted under a live pick, which is worth
      // seeing rather than rendering as a blank.
      provider_name: venue?.business_name ?? '(profile not found)',
      creator_name: creator?.business_name ?? '(profile not found)',
      // Raw, not normalised — see the RecentPick doc comment.
      creator_handle: creator?.instagram_handle ?? null,
    };
  });
}

/**
 * Flip a pick's `is_active`. Deactivate is the take-down; there is no hard
 * delete in this console (contract §3) and a deactivated row stays as history.
 *
 * Two things here are load-bearing:
 *
 *  * NO `.select()` chained — see the header note (2).
 *  * `{ count: 'exact' }` instead. It rides on `Prefer: count=exact`, which does
 *    NOT switch the return mode, so we keep the safe bare update AND learn how
 *    many rows moved. That matters because the Q-1 failure is SILENT: if the
 *    superAdmin SELECT policy is missing, the row is invisible to the UPDATE and
 *    Postgres reports success having changed nothing. Zero rows is reported as
 *    the error it is, rather than as a toast that lies.
 */
async function setPickActive(id: string, isActive: boolean): Promise<void> {
  const { error, count } = await supabase
    .from('creator_recommendations')
    .update({ is_active: isActive }, { count: 'exact' })
    .eq('id', id);
  if (error) throw describeError(error, 'Could not update that pick.');
  if (count === 0) {
    throw new Error(
      'That pick did not change — the database matched no row. Either it has just been '
      + 'removed, or this session is not the operator the row-level policy requires.',
    );
  }
}

/** Take a pick down (contract §3: never hard delete). */
export async function deactivatePick(id: string): Promise<void> {
  await setPickActive(id, false);
}

/** Put a deactivated pick back (LEAD RULING R1 / Q-1). */
export async function reactivatePick(id: string): Promise<void> {
  await setPickActive(id, true);
}
